import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
} from '@mui/material';
import { updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase';
import { 
  AdminPanelSettings as AdminIcon,
  DirectionsCar as DealershipIcon,
  Build as RepairIcon,
  Inventory as SpareIcon
} from '@mui/icons-material';

// Demo user configurations
const demoUsers = [
  {
    id: 'admin',
    name: 'System Administrator',
    email: 'admin@sparesync.demo',
    password: 'demo123456',
    role: 'admin',
    description: 'Full access to all features',
    icon: <AdminIcon />,
    color: '#d32f2f'
  },
  {
    id: 'dealership',
    name: 'Premium Dealership',
    email: 'dealership@sparesync.demo',
    password: 'demo123456',
    role: 'dealership',
    description: 'Dealership management features',
    icon: <DealershipIcon />,
    color: '#1976d2'
  },
  {
    id: 'repair-shop',
    name: 'Auto Repair Shop',
    email: 'repair@sparesync.demo',
    password: 'demo123456',
    role: 'repair-shop',
    description: 'Repair shop operations',
    icon: <RepairIcon />,
    color: '#388e3c'
  },
  {
    id: 'spare-shop',
    name: 'Spare Parts Store',
    email: 'spare@sparesync.demo',
    password: 'demo123456',
    role: 'spare-shop',
    description: 'Parts inventory management',
    icon: <SpareIcon />,
    color: '#f57c00'
  }
];

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDemoUsers, setShowDemoUsers] = useState(false);
  const { login, error, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for success message from signup
  useEffect(() => {
    if (location.state && (location.state as any).message) {
      setSuccessMessage((location.state as any).message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Function to get user-friendly error message
  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/invalid-login-credentials':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      default:
        return 'An error occurred during sign in. Please try again.';
    }
  };

  const handleDemoLogin = async (demoUser: typeof demoUsers[0]) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setEmail(demoUser.email);
    setPassword(demoUser.password);
    
    const result = await login(demoUser.email, demoUser.password);
    if (result) {
      // Set displayName to the role for proper routing
      try {
        await updateProfile(auth.currentUser!, { displayName: demoUser.role });
        
        // Navigate based on role
        switch (demoUser.role) {
          case 'admin':
            navigate('/dealership/dashboard');
            break;
          case 'repair-shop':
            navigate('/repair-shop/jobs');
            break;
          case 'spare-shop':
            navigate('/spare-shop/parts-search');
            break;
          default:
            navigate('/dealership/dashboard');
        }
      } catch (err) {
        console.error('Failed to update demo user profile:', err);
        setErrorMessage('Failed to set user privileges. Please try again.');
      }
    } else if (error) {
      setErrorMessage(getErrorMessage(error.code));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    
    // Special handling for admin user
    if (email.toLowerCase() === 'admin@ballout.co.za') {
      const result = await login(email, password);
      if (result) {
        // Set displayName to 'admin' for the admin user to grant access to all dashboards
        try {
          await updateProfile(auth.currentUser!, { displayName: 'admin' });
          navigate('/dealership/dashboard');
        } catch (err) {
          console.error('Failed to update admin profile:', err);
          setErrorMessage('Failed to set admin privileges. Please try again.');
        }
      } else if (error) {
        // Convert Firebase error to user-friendly message
        setErrorMessage(getErrorMessage(error.code));
      }
    } else {
      const result = await login(email, password);
      if (result) {
        // For non-admin users, navigate based on their role
        const userRole = result.user.displayName || 'dealership';
        
        switch (userRole) {
          case 'repair-shop':
            navigate('/repair-shop/jobs');
            break;
          case 'spare-shop':
            navigate('/spare-shop/parts-search');
            break;
          default:
            navigate('/dealership/dashboard');
        }
      } else if (error) {
        // Convert Firebase error to user-friendly message
        setErrorMessage(getErrorMessage(error.code));
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={4}>
          {/* Demo Users Section */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, height: 'fit-content' }}>
              <Typography component="h1" variant="h5" align="center" gutterBottom>
                🚀 Demo Access
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Click any user below to instantly access their dashboard
              </Typography>
              
              <Grid container spacing={2}>
                {demoUsers.map((user) => (
                  <Grid item xs={12} key={user.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        }
                      }}
                      onClick={() => handleDemoLogin(user)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ 
                            color: user.color, 
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {user.icon}
                          </Box>
                          <Typography variant="h6" component="div">
                            {user.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {user.description}
                        </Typography>
                        <Chip 
                          label={user.role.toUpperCase()} 
                          size="small" 
                          sx={{ 
                            backgroundColor: user.color,
                            color: 'white',
                            fontSize: '0.7rem'
                          }} 
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Regular Login Section */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography component="h1" variant="h5" align="center" gutterBottom>
                Sign In
              </Typography>
              {(errorMessage || successMessage) && (
                <Alert severity={errorMessage ? "error" : "success"} sx={{ mb: 2 }}>
                  {errorMessage || successMessage}
                </Alert>
              )}
              <form onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                      <Typography variant="body2" color="primary">
                        Forgot password?
                      </Typography>
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link to="/signup" style={{ textDecoration: 'none' }}>
                      <Typography variant="body2" color="primary">
                        Don't have an account? Sign Up
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}; 